/**
 * MeshNode — the runtime peer.
 *
 * Wraps libp2p, Helia (modern IPFS (InterPlanetary File System)), and OrbitDB
 * v3 into a single lifecycle-managed object. Owns the heavy P2P (Peer-to-Peer)
 * dependencies; every other layer depends only on this module's interface.
 *
 * Boundary discipline: this is the ONLY file that imports libp2p / Helia /
 * OrbitDB. They're dynamically imported so a build using only `MockMeshNode`
 * (single-node dev, tests, demos) tree-shakes them out.
 *
 * Architectural decision — sync-layer identity is independent of application
 * identity. This module does NOT thread the SignerIdentity through to
 * OrbitDB's log entry signatures. Rationale:
 *
 *   1. OrbitDB v3+ has a single identity per OrbitDB instance set at
 *      construction. It does not accept per-database identities. Trying to
 *      open a different identity per log isn't possible without spinning up
 *      multiple OrbitDB instances — too heavy.
 *
 *   2. OrbitDB's log entry signature serves ACL (Access Control List) and
 *      replication-layer integrity, not application authenticity. Our
 *      `SignedEnvelope` (signed by `SignerIdentity` with context) is the
 *      application-level cryptographic source of truth.
 *
 *   3. The two-layer model is well-known: TLS layer has its own keys
 *      independent of any application auth on top. Same pattern here.
 *
 * What this means for hierarchical identity: per-context unlinkability is
 * preserved at the envelope layer (the only layer that matters for
 * application authenticity). A network observer of OrbitDB pubsub traffic
 * sees a single per-device sync key, but the envelopes inside each log are
 * still per-context unlinkable signatures. Document this clearly in
 * `docs/orbitdb-sync.md`.
 *
 * Versions targeted (verified May 2026):
 *   - libp2p ^3.0.0 (note: maintainer transition in progress; API may shift)
 *   - helia ^6.1.4
 *   - @orbitdb/core ^3.0.2
 *   - @chainsafe/libp2p-gossipsub ^14.1.1
 *
 * If `pnpm install` fails with peer dep resolution errors, the most likely
 * culprits are individual `@libp2p/*` packages I extrapolated to v3-era
 * majors. Adjust their minor/major bounds to whatever satisfies libp2p ^3.
 */

import type { SignedEnvelope } from '../envelope/Envelope';

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC INTERFACE
// ─────────────────────────────────────────────────────────────────────────────

export interface MeshNodeOptions {
  /** Bootstrap multiaddrs for initial peer discovery. */
  bootstrap?: string[];
  /** OrbitDB directory hint for keystore + databases. Browser uses IndexedDB regardless. */
  directory?: string;
  /** Override the OrbitDB identity ID. Default: auto-generated by OrbitDB. */
  orbitId?: string;
}

export interface MeshNodeStats {
  peerId: string;
  connected: number;
  uptime: number;
  /** Last replication event timestamp. */
  lastSyncAt: number | null;
}

/**
 * EventLog — the surface of an OrbitDB events database we depend on.
 *
 * Matches the OrbitDB v3 events DB API as documented at api.orbitdb.org:
 *   - add(value): Promise<string>
 *   - get(hash): Promise<value>
 *   - all(): Promise<Array<{ hash, value }>>
 *   - iterator(filters?): AsyncGenerator<[hash, value]>
 *   - close(): Promise<void>
 *   - events: EventEmitter for 'update' | 'join' | 'leave' | 'close' | 'drop'
 *   - address: { toString(): string }
 *   - peers: Set<PeerID>
 */
export interface EventLogEntry {
  /** The CID-like hash of the oplog entry. */
  hash: string;
  /** The payload — for us, always a SignedEnvelope. */
  value: SignedEnvelope;
}

export interface EventLog {
  address: { toString: () => string };
  peers: Set<unknown>;
  add(value: SignedEnvelope): Promise<string>;
  get(hash: string): Promise<SignedEnvelope | null>;
  all(): Promise<Array<{ hash: string; value: SignedEnvelope }>>;
  iterator(filters?: {
    gt?: string;
    gte?: string;
    lt?: string;
    lte?: string;
    amount?: number;
  }): AsyncGenerator<[string, SignedEnvelope]>;
  close(): Promise<void>;
  events: {
    on(event: 'update', cb: (entry: EventLogEntry) => void): void;
    on(event: 'join', cb: (peerId: unknown, heads: unknown[]) => void): void;
    on(event: 'leave', cb: (peerId: unknown) => void): void;
    on(event: 'close' | 'drop', cb: () => void): void;
    off(event: string, cb: (...args: unknown[]) => void): void;
  };
}

export interface MeshNode {
  start(): Promise<void>;
  stop(): Promise<void>;
  /** Open an OrbitDB events log by name or full address. */
  openLog(nameOrAddress: string): Promise<EventLog>;
  /** libp2p handle for advanced operations (peer dial, pubsub introspection). */
  libp2p(): unknown;
  /** Helia handle for direct blockstore ops. */
  helia(): unknown;
  stats(): Promise<MeshNodeStats>;
  readonly peerId: string;
  readonly started: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// MOCK NODE — runs the system without any real P2P stack
// ─────────────────────────────────────────────────────────────────────────────

export class MockMeshNode implements MeshNode {
  readonly peerId = 'mock-peer-' + Math.random().toString(36).slice(2, 8);
  started = false;
  #startedAt = 0;

  async start(): Promise<void> {
    this.started = true;
    this.#startedAt = Date.now();
  }

  async stop(): Promise<void> {
    this.started = false;
  }

  async openLog(_name: string): Promise<EventLog> {
    throw new Error('MockMeshNode does not support openLog — use createRealMeshNode');
  }

  libp2p(): unknown { return null; }
  helia(): unknown { return null; }

  async stats(): Promise<MeshNodeStats> {
    return {
      peerId: this.peerId,
      connected: 0,
      uptime: this.started ? Date.now() - this.#startedAt : 0,
      lastSyncAt: null,
    };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// REAL NODE — boots Helia + libp2p + OrbitDB via dynamic imports
// ─────────────────────────────────────────────────────────────────────────────

// Opaque types for the dynamically-imported stack. We deliberately avoid
// importing types from libp2p/helia/orbitdb at module level so the heavy
// dependencies tree-shake out of builds that don't call createRealMeshNode.

interface Libp2pHandle {
  peerId: { toString(): string };
  start(): Promise<void>;
  stop(): Promise<void>;
  getPeers(): unknown[];
  getMultiaddrs(): Array<{ toString(): string }>;
  dial(addr: string | unknown): Promise<unknown>;
}

interface HeliaHandle {
  libp2p: Libp2pHandle;
  stop(): Promise<void>;
}

interface OrbitDBHandle {
  open(addressOrName: string, opts?: { type?: string }): Promise<EventLog>;
  stop(): Promise<void>;
  identity: { id: string };
}

class RealMeshNode implements MeshNode {
  readonly peerId: string;
  started = false;
  #libp2p: Libp2pHandle;
  #helia: HeliaHandle;
  #orbit: OrbitDBHandle;
  #startedAt = 0;
  #lastSyncAt: number | null = null;
  #openLogs = new Set<EventLog>();

  constructor(args: {
    libp2p: Libp2pHandle;
    helia: HeliaHandle;
    orbit: OrbitDBHandle;
  }) {
    this.#libp2p = args.libp2p;
    this.#helia = args.helia;
    this.#orbit = args.orbit;
    this.peerId = args.libp2p.peerId.toString();
  }

  async start(): Promise<void> {
    if (this.started) return;
    // libp2p starts automatically inside createLibp2p() by default. Call
    // start() defensively in case the user explicitly set { start: false }.
    await this.#libp2p.start().catch(() => { /* already started, fine */ });
    this.started = true;
    this.#startedAt = Date.now();
  }

  async stop(): Promise<void> {
    if (!this.started) return;
    // Close any open logs first so OrbitDB's internal cleanup runs before
    // we tear down the underlying instances. The canonical shutdown order
    // (per OrbitDB docs) is: db.close() → orbitdb.stop() → helia.stop().
    for (const log of this.#openLogs) {
      try { await log.close(); } catch { /* swallow */ }
    }
    this.#openLogs.clear();
    await this.#orbit.stop();
    await this.#helia.stop();
    // helia.stop() stops the underlying libp2p, but call defensively.
    await this.#libp2p.stop().catch(() => { /* already stopped */ });
    this.started = false;
  }

  async openLog(nameOrAddress: string): Promise<EventLog> {
    if (!this.started) {
      throw new Error('MeshNode not started — call start() before openLog');
    }
    // Default type is 'events' per OrbitDB v3 docs.
    const log = await this.#orbit.open(nameOrAddress, { type: 'events' });
    // Track for cleanup on stop().
    this.#openLogs.add(log);
    // Mark replication activity for stats.
    log.events.on('update', () => {
      this.#lastSyncAt = Date.now();
    });
    log.events.on('join', () => {
      this.#lastSyncAt = Date.now();
    });
    return log;
  }

  libp2p(): unknown { return this.#libp2p; }
  helia(): unknown { return this.#helia; }

  async stats(): Promise<MeshNodeStats> {
    return {
      peerId: this.peerId,
      connected: this.#libp2p.getPeers().length,
      uptime: this.started ? Date.now() - this.#startedAt : 0,
      lastSyncAt: this.#lastSyncAt,
    };
  }
}

/**
 * Boot the real Helia + libp2p + OrbitDB stack.
 *
 * Browser-targeted libp2p config (verified May 2026):
 *
 *   - transports: webSockets, webRTC, circuitRelayTransport
 *     (browsers can't listen on raw TCP; WebRTC needs a circuit relay for
 *     browser-to-browser SDP signaling)
 *   - connectionEncrypters: [noise()]   (PLURAL; libp2p v3 renamed)
 *   - streamMuxers: [yamux()]
 *   - services: { identify, pubsub: gossipsub(...) }
 *   - connectionGater: { denyDialMultiaddr: () => false }
 *     (lets us dial private addresses for local testing — production should
 *      tighten this)
 *
 * All imports happen dynamically inside this function so a build that only
 * uses LocalSyncProvider + MockMeshNode never loads them.
 */
export async function createRealMeshNode(opts: MeshNodeOptions = {}): Promise<MeshNode> {
  // Dynamic imports — only execute when this function is actually called.
  const [
    { createLibp2p },
    { webRTC },
    { webSockets },
    { circuitRelayTransport },
    { noise },
    { yamux },
    { gossipsub },
    { identify },
    { bootstrap },
    { createHelia },
    { IDBBlockstore },
    { createOrbitDB },
  ] = await Promise.all([
    import('libp2p'),
    import('@libp2p/webrtc'),
    import('@libp2p/websockets'),
    import('@libp2p/circuit-relay-v2'),
    import('@chainsafe/libp2p-noise'),
    import('@chainsafe/libp2p-yamux'),
    import('@chainsafe/libp2p-gossipsub'),
    import('@libp2p/identify'),
    import('@libp2p/bootstrap'),
    import('helia'),
    import('blockstore-idb'),
    // @ts-expect-error — @orbitdb/core ships JS without type declarations
    import('@orbitdb/core'),
  ]) as [
    { createLibp2p: (config: unknown) => Promise<Libp2pHandle> },
    { webRTC: () => unknown },
    { webSockets: () => unknown },
    { circuitRelayTransport: () => unknown },
    { noise: () => unknown },
    { yamux: () => unknown },
    { gossipsub: (config: unknown) => unknown },
    { identify: () => unknown },
    { bootstrap: (config: { list: string[] }) => unknown },
    { createHelia: (config: { libp2p: Libp2pHandle; blockstore?: unknown }) => Promise<HeliaHandle> },
    { IDBBlockstore: new (name: string) => unknown },
    { createOrbitDB: (config: { ipfs: HeliaHandle; id?: string; directory?: string }) => Promise<OrbitDBHandle> },
  ];

  // ── 1. libp2p (browser-targeted) ──────────────────────────
  const libp2pConfig: Record<string, unknown> = {
    transports: [
      webSockets(),
      webRTC(),
      // circuitRelayTransport is required for browser-to-browser WebRTC:
      // browsers need a relay to forward the SDP signaling messages until
      // the direct WebRTC connection is established.
      circuitRelayTransport(),
    ],
    connectionEncrypters: [noise()],
    streamMuxers: [yamux()],
    connectionGater: {
      // Allow private addresses for local testing. Tighten in production.
      denyDialMultiaddr: async (): Promise<boolean> => false,
    },
    services: {
      identify: identify(),
      // gossipsub is OrbitDB's required pubsub layer for replication.
      // allowPublishToZeroTopicPeers lets a single peer publish before peers
      // have joined — required for the first writer to a new log.
      pubsub: gossipsub({
        allowPublishToZeroTopicPeers: true,
        emitSelf: false,
      }),
    },
    addresses: {
      // Browsers can't listen on raw TCP. They listen via circuit relay for
      // incoming WebRTC connections, which requires `/p2p-circuit` in the
      // listen list.
      listen: ['/p2p-circuit', '/webrtc'],
    },
  };
  if (opts.bootstrap && opts.bootstrap.length > 0) {
    libp2pConfig.peerDiscovery = [bootstrap({ list: opts.bootstrap })];
  }
  const libp2p = await createLibp2p(libp2pConfig);

  // ── 2. Helia with persistent IndexedDB blockstore ────────
  // Default Helia uses an in-memory blockstore which loses everything on
  // page reload. We want IndexedDB persistence so envelopes survive across
  // sessions.
  //
  // blockstore-idb 2.x DROPPED the auto-open behavior — callers must call
  // .open() explicitly before passing to Helia, or the first read throws
  // "Blockstore needs to be opened." (visible to the user as a red error
  // on the UnlockSurface when network mode = mesh). See FIX.5.
  const blockstoreName = opts.directory
    ? `surface-mesh-${opts.directory}`
    : 'surface-mesh-blocks';
  const blockstore = new IDBBlockstore(blockstoreName) as { open: () => Promise<void> } & object;
  await blockstore.open();
  const helia = await createHelia({ libp2p, blockstore });

  // ── 3. OrbitDB ────────────────────────────────────────────
  // No explicit identity passed — OrbitDB will create one and store it in
  // its KeyStore. This is the sync-layer identity, independent of our
  // application-level SignerIdentity. See the file header comment for the
  // rationale.
  const orbitOpts: Parameters<typeof createOrbitDB>[0] = { ipfs: helia };
  if (opts.orbitId !== undefined) orbitOpts.id = opts.orbitId;
  if (opts.directory !== undefined) orbitOpts.directory = opts.directory;
  const orbit = await createOrbitDB(orbitOpts);

  return new RealMeshNode({ libp2p, helia, orbit });
}
